import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import styled from "@emotion/styled";
import { Switch } from "../../../UI/Switch";
import { COLOR_MISCHKA, COLOR_STALE_GRAY } from "../../../constants/styles";
import { LoadPortfolioIntegrations, PorfolioIntegraion, GeneratePortfolioApiKey, UpdatePortfolioIntegration } from "../../../services/PortfolioApi";
import useAuth from "../../login/AuthContext";
import { Table } from "../../../UI/Table";
import { TableHead } from "../../../UI/TableHead";
import { TableRow } from "../../../UI/TableRow";
import { TableCell } from "../../../UI/TableCell";
import { Button } from "../../../UI/Button";
import { Typography } from "../../../UI/Typography";
import { useSnackbar } from "../../../hooks/useSnackbar";
import { ModalProvider } from "../../../contexts/ModalContext";
import { CreateIntegrationModal } from "./CreateIntegrationModal";
import { useModalContext } from "../../../contexts/ModalContext/context";

const StyledSwitch = styled(Switch)`
  opacity: ${props => props.disabled ? '0.5' : '1'};

  .MuiSwitch-switchBase.Mui-disabled {
    color: ${COLOR_STALE_GRAY} !important;

    & + .MuiSwitch-track {
      background-color: ${COLOR_MISCHKA} !important;
      opacity: 1 !important;
    }

    & .MuiSwitch-thumb {
      background-color: ${COLOR_STALE_GRAY} !important;
    }
  }

  .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb {
    background-color: ${COLOR_STALE_GRAY} !important;
  }
`;




interface IntegrationRowProps {
    integration: PorfolioIntegraion;
    portfolioId: string;
  }

  const IntegrationRow: React.FC<IntegrationRowProps> = ({ integration, portfolioId }) => {
    const [enabled, setEnabled] = useState(integration.enabled);
    const { getAuthToken } = useAuth();
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();

    // Update local state when integration data changes
    useEffect(() => {
      setEnabled(integration.enabled);
    }, [integration.enabled]);

    const handleSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const newEnabled = event.target.checked;

      try {
        const token = await getAuthToken();
        const result = await UpdatePortfolioIntegration(
          portfolioId,
          integration.id,
          newEnabled,
          integration.importType,
          token
        );

        if (result.ok) {
          setEnabled(newEnabled); // Update local state to reflect the change
          showSuccessSnackbar(`Integration ${newEnabled ? 'enabled' : 'disabled'} successfully`);
        } else {
          showErrorSnackbar('Failed to update integration settings');
        }
      } catch (error) {
        showErrorSnackbar('Failed to update integration settings');
      }
    };

    return (
      <TableRow>
        <TableCell>{integration.importType}</TableCell>
        <TableCell>{integration.domain || ''}</TableCell>
        <TableCell>
          <StyledSwitch
            checked={enabled}
            onChange={handleSwitchChange}
            onClick={(e) => e.stopPropagation()} // Prevent row click when clicking the switch
          />
        </TableCell>
      </TableRow>
    );
  };

const CreateIntegrationButton: React.FC<{ portfolioId: string; onSuccess: () => void }> = ({ portfolioId, onSuccess }) => {
  const { setIsOpen } = useModalContext();

  const handleClick = () => {
    setIsOpen(true);
  };

  return (
    <>
      <Button appearance="primary" onClick={handleClick}>
        Create
      </Button>
      <CreateIntegrationModal portfolioId={portfolioId} onSuccess={onSuccess} />
    </>
  );
};

interface PortfolioIntegrationsTabProps {
  portfolioId: string;
}

export const PortfolioIntegrationsTab: React.FC<PortfolioIntegrationsTabProps> = ({ portfolioId }) => {
  const { getAuthToken } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [integrations, setIntegrations] = useState<PorfolioIntegraion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiKey, setApiKey] = useState<string>("");
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);

  const loadIntegrations = async () => {
    try {
      setIsLoading(true);
      const response = await LoadPortfolioIntegrations(portfolioId, await getAuthToken());
      if (response?.settings) {
        setIntegrations(response.settings);
      }
    } catch (error) {
      // console.error('Failed to load portfolio integrations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateApiKey = async () => {
    try {
      setIsGeneratingKey(true);
      const authToken = await getAuthToken();
      const response = await GeneratePortfolioApiKey(portfolioId, authToken);

      if (response && 'apiKey' in response) {
        setApiKey(response.apiKey);
        showSuccessSnackbar('API key generated successfully');
      }
    } catch (error) {
      showErrorSnackbar('Failed to generate API key');
    } finally {
      setIsGeneratingKey(false);
    }
  };

  useEffect(() => {
    loadIntegrations();
  }, [portfolioId]);

  if (isLoading) {
    return <>Loading...</>;
  }

  return (
    <Grid container direction="row" spacing={3}>
      <Grid item xs={12}>
        <Grid container mt={1} justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Connections</Typography>
          <ModalProvider>
            <CreateIntegrationButton portfolioId={portfolioId} onSuccess={loadIntegrations} />
          </ModalProvider>
        </Grid>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={300}>Type</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell width={100} padding="checkbox" align="left">Enabled</TableCell>
            </TableRow>
          </TableHead>
          {integrations.map(integration => (
            <IntegrationRow
              key={integration.id}
              integration={integration}
              portfolioId={portfolioId}
            />
          ))}
        </Table>
      </Grid>

      <Grid item xs={12}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={300}>Roomsync API Keys</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableRow>
            <TableCell>Roommate Relations Export</TableCell>
            <TableCell align="right">
              {apiKey ? (
                <Typography>{apiKey}</Typography>
              ) : (
                <Button
                  onClick={handleGenerateApiKey}
                  isLoading={isGeneratingKey}
                >
                  Generate
                </Button>
              )}
            </TableCell>
          </TableRow>
        </Table>
      </Grid>
    </Grid>
  );
};
